import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../services/api';
import { MainLayout } from '../components/shared/MainLayout';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Plus, X, Stethoscope, Phone, Envelope, MapPin,
  Star, PencilSimple, Trash, Spinner, CaretDown, FirstAidKit, Buildings
} from '@phosphor-icons/react';

const ROLE_OPTIONS = [
  'GP', 'Cardiologist', 'Psychiatrist', 'Psychologist', 'Endocrinologist',
  'Neurologist', 'Rheumatologist', 'Dermatologist', 'Oncologist',
  'Gastroenterologist', 'Pulmonologist', 'Nephrologist', 'Urologist',
  'Orthopaedic Surgeon', 'Physiotherapist', 'Dietitian', 'Pharmacist',
  'Dentist', 'Optometrist', 'Podiatrist', 'Speech Pathologist',
  'Occupational Therapist', 'Surgeon', 'Anaesthetist', 'Radiologist',
  'Pathologist', 'Other',
];

const EMPTY_FORM = {
  name: '', role: '', specialty: '', clinic_name: '', phone: '',
  email: '', fax: '', location: '', notes: '', linked_conditions: [],
  status: 'current', is_primary: false, professional_context: '',
};

const MedicalTeamPage = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [conditions, setConditions] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  const fetchTeam = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/medical-team`);
      setMembers(data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  const fetchConditions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/conditions`);
      setConditions(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchTeam(); fetchConditions(); }, [fetchTeam, fetchConditions]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setShowForm(true);
  };

  const openEdit = (m) => {
    setEditing(m.id);
    setForm({
      name: m.name || '', role: m.role || '', specialty: m.specialty || '',
      clinic_name: m.clinic_name || '', phone: m.phone || '', email: m.email || '',
      fax: m.fax || '', location: m.location || '', notes: m.notes || '',
      linked_conditions: m.linked_conditions || [], status: m.status || 'current',
      is_primary: m.is_primary || false, professional_context: m.professional_context || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/medical-team/${editing}`, form);
        toast.success('Saved — I\'ve got their details');
      } else {
        await axios.post(`${API}/medical-team`, form);
        toast.success('Added to your care team');
      }
      setShowForm(false);
      setEditing(null);
      await fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this team member?')) return;
    try {
      await axios.delete(`${API}/medical-team/${id}`);
      toast.success('Team member removed');
      await fetchTeam();
    } catch { toast.error('Failed to remove'); }
  };

  const toggleCondition = (condName) => {
    setForm(f => ({
      ...f,
      linked_conditions: f.linked_conditions.includes(condName)
        ? f.linked_conditions.filter(c => c !== condName)
        : [...f.linked_conditions, condName]
    }));
  };

  const current = members.filter(m => m.status !== 'past');
  const past = members.filter(m => m.status === 'past');

  return (
    <MainLayout>
      <div className="max-w-lg mx-auto w-full" data-testid="medical-team-page">
        {/* Header */}
        <header className="glass-header sticky top-0 z-10 px-4 py-3 flex items-center justify-between safe-top min-h-[48px]">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/health')} className="text-slate-500 hover:text-slate-900" data-testid="team-back">
              <ArrowLeft size={22} />
            </button>
            <div>
              <h1 className="font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Care Team</h1>
              <p className="text-xs text-slate-400">{current.length} active clinician{current.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <button onClick={openCreate} className="flex items-center gap-1.5 px-3.5 py-2 bg-[#235347] text-white rounded-lg text-sm font-medium hover:bg-[#1A3F36] transition-colors" data-testid="add-team-member-btn">
            <Plus size={16} weight="bold" /> Add
          </button>
        </header>

        <div className="px-6 pb-8 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner size={28} className="animate-spin text-[#235347]" />
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-16" data-testid="team-empty-state">
              <Stethoscope size={48} className="mx-auto text-slate-200 mb-4" weight="duotone" />
              <h3 className="text-sm font-medium text-slate-600 mb-1">No care team yet</h3>
              <p className="text-xs text-slate-400 max-w-[260px] mx-auto">
                Add your doctors, specialists and allied health professionals so Kindra can tailor your summaries.
              </p>
              <button onClick={openCreate} className="mt-4 px-5 py-2.5 bg-[#235347] text-white rounded-lg text-sm font-medium hover:bg-[#1A3F36] transition-colors" data-testid="add-first-member-btn">
                Add your first clinician
              </button>
            </div>
          ) : (
            <>
              {/* Current team */}
              {current.length > 0 && (
                <div className="space-y-2.5 mt-4">
                  {current.map((m) => (
                    <MemberCard key={m.id} member={m} expanded={expandedId === m.id}
                      onToggle={() => setExpandedId(expandedId === m.id ? null : m.id)}
                      onEdit={() => openEdit(m)} onDelete={() => handleDelete(m.id)} />
                  ))}
                </div>
              )}
              {/* Past clinicians */}
              {past.length > 0 && (
                <div className="mt-6">
                  <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-2">Past Clinicians</p>
                  <div className="space-y-2 opacity-60">
                    {past.map((m) => (
                      <MemberCard key={m.id} member={m} expanded={expandedId === m.id}
                        onToggle={() => setExpandedId(expandedId === m.id ? null : m.id)}
                        onEdit={() => openEdit(m)} onDelete={() => handleDelete(m.id)} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Create / Edit Form Modal */}
        <AnimatePresence>
          {showForm && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center sm:justify-center"
              onClick={() => setShowForm(false)}>
              <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white w-full sm:w-[480px] sm:rounded-2xl rounded-t-3xl max-h-[85vh] overflow-auto"
                data-testid="team-member-form">
                <div className="sticky top-0 bg-white px-6 pt-6 pb-3 border-b border-slate-100 flex items-center justify-between z-10">
                  <h2 className="text-lg font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {editing ? 'Edit Clinician' : 'Add Clinician'}
                  </h2>
                  <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600">
                    <X size={20} />
                  </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                  {/* Name */}
                  <div>
                    <label className="text-xs text-slate-500 font-medium mb-1 block">Name *</label>
                    <input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="Dr. Jane Smith" data-testid="team-form-name"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#235347]/20" />
                  </div>

                  {/* Role + Specialty */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500 font-medium mb-1 block">Role / Type</label>
                      <select value={form.role} onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))}
                        data-testid="team-form-role"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#235347]/20">
                        <option value="">Select...</option>
                        {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 font-medium mb-1 block">Specialty</label>
                      <input value={form.specialty} onChange={(e) => setForm(f => ({ ...f, specialty: e.target.value }))}
                        placeholder="e.g. Interventional" data-testid="team-form-specialty"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#235347]/20" />
                    </div>
                  </div>

                  {/* Clinic */}
                  <div>
                    <label className="text-xs text-slate-500 font-medium mb-1 block">Clinic / Practice</label>
                    <input value={form.clinic_name} onChange={(e) => setForm(f => ({ ...f, clinic_name: e.target.value }))}
                      placeholder="Name of practice" data-testid="team-form-clinic"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#235347]/20" />
                  </div>

                  {/* Phone + Email */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500 font-medium mb-1 block">Phone</label>
                      <input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                        placeholder="+61 4..." data-testid="team-form-phone"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#235347]/20" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 font-medium mb-1 block">Email</label>
                      <input value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                        placeholder="contact@clinic.com" data-testid="team-form-email"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#235347]/20" />
                    </div>
                  </div>

                  {/* Fax + Location */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500 font-medium mb-1 block">Fax</label>
                      <input value={form.fax} onChange={(e) => setForm(f => ({ ...f, fax: e.target.value }))}
                        placeholder="Optional" data-testid="team-form-fax"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#235347]/20" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 font-medium mb-1 block">Location</label>
                      <input value={form.location} onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))}
                        placeholder="Suburb or address" data-testid="team-form-location"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#235347]/20" />
                    </div>
                  </div>

                  {/* Status + Primary */}
                  <div className="grid grid-cols-2 gap-3 items-end">
                    <div>
                      <label className="text-xs text-slate-500 font-medium mb-1 block">Status</label>
                      <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
                        data-testid="team-form-status"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#235347]/20">
                        <option value="current">Current</option>
                        <option value="past">Past</option>
                      </select>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer pb-3">
                      <input type="checkbox" checked={form.is_primary}
                        onChange={(e) => setForm(f => ({ ...f, is_primary: e.target.checked }))}
                        className="rounded border-slate-300 text-[#235347] focus:ring-[#235347]"
                        data-testid="team-form-primary" />
                      Primary clinician
                    </label>
                  </div>

                  {/* Linked conditions */}
                  {conditions.length > 0 && (
                    <div>
                      <label className="text-xs text-slate-500 font-medium mb-2 block">Manages these conditions</label>
                      <div className="flex flex-wrap gap-2" data-testid="team-form-conditions">
                        {conditions.map(c => {
                          const selected = form.linked_conditions.includes(c.name);
                          return (
                            <button key={c.id || c.name} onClick={() => toggleCondition(c.name)}
                              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                selected ? 'bg-[#235347] text-white border-[#235347]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#235347]'
                              }`} data-testid={`condition-chip-${c.name}`}>
                              {c.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Professional context */}
                  <div>
                    <label className="text-xs text-slate-500 font-medium mb-1 block">Professional Context</label>
                    <textarea value={form.professional_context}
                      onChange={(e) => setForm(f => ({ ...f, professional_context: e.target.value }))}
                      placeholder="Specialty focus, subspecialty, practice scope (factual only)"
                      rows={2} data-testid="team-form-context"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#235347]/20 resize-none" />
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="text-xs text-slate-500 font-medium mb-1 block">Notes</label>
                    <textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                      placeholder="Anything helpful to remember" rows={2} data-testid="team-form-notes"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#235347]/20 resize-none" />
                  </div>
                </div>

                <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-slate-100">
                  <button onClick={handleSave} disabled={saving}
                    className="w-full bg-[#235347] text-white rounded-full py-3 font-medium hover:bg-[#1A3F36] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="team-form-save">
                    {saving ? <Spinner size={18} className="animate-spin" /> : null}
                    {editing ? 'Update Clinician' : 'Add Clinician'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </MainLayout>
  );
};

/* ── Member Card ── */
const MemberCard = ({ member: m, expanded, onToggle, onEdit, onDelete }) => (
  <div className="card-base overflow-hidden" data-testid="team-member-card">
    <div className="p-4 cursor-pointer" onClick={onToggle} role="button" tabIndex={0}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
          m.is_primary ? 'bg-[#235347]/10 text-[#235347]' : 'bg-slate-100 text-slate-400'
        }`}>
          <Stethoscope size={20} weight={m.is_primary ? 'fill' : 'regular'} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-slate-900 truncate">{m.name}</p>
            {m.is_primary && (
              <Star size={14} weight="fill" className="text-amber-500 flex-shrink-0" />
            )}
          </div>
          <p className="text-xs text-slate-500">
            {m.role}{m.specialty ? ` · ${m.specialty}` : ''}
          </p>
          {m.clinic_name && (
            <p className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1">
              <Buildings size={10} /> {m.clinic_name}
            </p>
          )}
          {m.linked_conditions?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {m.linked_conditions.map(c => (
                <span key={c} className="text-[9px] px-1.5 py-0.5 bg-[#235347]/8 text-[#235347] rounded">
                  {c}
                </span>
              ))}
            </div>
          )}
        </div>
        <CaretDown size={16} className={`text-slate-300 transition-transform flex-shrink-0 mt-1 ${expanded ? 'rotate-180' : ''}`} />
      </div>
    </div>

    <AnimatePresence>
      {expanded && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
          <div className="px-4 pb-4 pt-0 border-t border-slate-100">
            <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
              {m.phone && (
                <a href={`tel:${m.phone}`} className="flex items-center gap-1.5 text-slate-600 hover:text-[#235347]" data-testid="member-phone">
                  <Phone size={14} className="text-slate-400" /> {m.phone}
                </a>
              )}
              {m.email && (
                <a href={`mailto:${m.email}`} className="flex items-center gap-1.5 text-slate-600 hover:text-[#235347]" data-testid="member-email">
                  <Envelope size={14} className="text-slate-400" /> {m.email}
                </a>
              )}
              {m.location && (
                <p className="flex items-center gap-1.5 text-slate-500 col-span-2" data-testid="member-location">
                  <MapPin size={14} className="text-slate-400" /> {m.location}
                </p>
              )}
              {m.fax && (
                <p className="flex items-center gap-1.5 text-slate-500">
                  <FirstAidKit size={14} className="text-slate-400" /> Fax: {m.fax}
                </p>
              )}
            </div>
            {m.professional_context && (
              <p className="text-xs text-slate-500 mt-3 bg-slate-50 rounded-lg p-2.5">
                {m.professional_context}
              </p>
            )}
            {m.notes && (
              <p className="text-xs text-slate-400 mt-2 italic">{m.notes}</p>
            )}
            <div className="flex items-center gap-2 mt-3">
              <button onClick={(e) => { e.stopPropagation(); onEdit(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                data-testid="edit-team-member">
                <PencilSimple size={12} /> Edit
              </button>
              <button onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-500 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                data-testid="delete-team-member">
                <Trash size={12} /> Remove
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default MedicalTeamPage;
